import React, { useEffect, useRef, useState } from 'react';
import * as qs from 'qs';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@mui/material/Alert';
import i18n from '../i18n/i18n';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSession } from '../contexts/SessionContext';
import Header from './TopHeader';
import LoadingOverlay from './overlay/LoadingOverlay';
import { closeSession } from '../api/api';
import { SPG_CARD_RETURN_PARAM, toBase64UrlSessionId } from '../utils/spgCardReturn';

const LogoContainer = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  justify-content: center;
  bottom: 0;
  position: fixed;
  padding: 10px;
  box-shadow: rgba(0, 0, 0, 0.6) 0 2px 5px;
  background-color: white;
`;

function SpgCard({ location }) {
  const { s } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { vendingMachineInfo } = useVendingMachine();
  const { sessionId, reset } = useSession();
  const history = useHistory();
  const { theme } = useTheme();

  // The SIBS form context returned by POST /vendingMachine/session/open
  // (gatewayName: 'SPG', gatewayParameters: { method: 'CARD' }), handed over
  // via router state by startCardPayment (VMView / ActionButtons).
  const spgCard = location?.state?.spgCard;

  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cancel: best-effort teardown of the open card session, then back home.
  const onBack = async () => {
    try {
      if (sessionId?.current) {
        await closeSession(sessionId.current);
      }
    } catch (e) {
      // ignore — best-effort cleanup
    }
    reset();
    history.replace(s ? `/home?s=${s}` : '/home');
  };

  // Recover if there is no machine / no form context (deep link or hard reload
  // that dropped the router state) — there is nothing to render, so go back.
  useEffect(() => {
    if (!vendingMachineInfo) {
      history.replace(s ? `/load?s=${s}` : '/home-no-vm');
      return;
    }

    if (!spgCard?.formContext) {
      history.replace(s ? `/home?s=${s}` : '/home');
    }
  }, [vendingMachineInfo, spgCard, s, history]);

  // Render the SIBS hosted form: set its attributes, then inject the widget
  // script (it scans the DOM for form.paymentSPG and hydrates it). When the
  // payment finishes SIBS redirects the browser to spg-config.redirectUrl (a full
  // page load): the live session screen sees the card flag, asks the server once
  // for the payment status, and follows the session through the ?sid= poll.
  useEffect(() => {
    const form = formRef.current;
    if (!form || !spgCard?.formContext || !spgCard?.widgetScriptUrl) {
      return undefined;
    }

    const sid = sessionId?.current;
    let query = '';
    if (sid) {
      query = `?sid=${toBase64UrlSessionId(sid)}&${SPG_CARD_RETURN_PARAM}=1`;
    } else if (s) {
      query = `?s=${s}`;
    }
    const redirectUrl = `${window.location.origin}/product-selection${query}`;

    form.setAttribute('spg-context', spgCard.formContext);

    // SIBS needs a full spg-config to build the form. Sending only redirectUrl leaves the
    // form with no amount/methods, so it errors with "No TransactionID". Fields per the SIBS
    // form-integration docs: amount, language, redirectUrl, paymentMethodList, customerData.
    // The amount isn't passed separately, so read it from the (base64 JSON) formContext.
    let amount: { value: number; currency: string } | undefined;
    try {
      const ctx = JSON.parse(atob(spgCard.formContext));
      if (ctx?.Amount) {
        amount = { value: ctx.Amount.Amount, currency: ctx.Amount.Currency };
      }
    } catch (e) {
      // leave amount undefined; the widget still gets the rest of the config
    }

    const spgConfig: Record<string, unknown> = {
      paymentMethodList: ['CARD'],
      language: (i18n.language || 'en').slice(0, 2),
      redirectUrl,
      customerData: null,
    };
    if (amount) {
      spgConfig.amount = amount;
    }

    form.setAttribute('spg-config', JSON.stringify(spgConfig));

    // Theme the SIBS form to match the app (spg-style per the form-integration docs). The docs
    // don't document a mobile layout preset, so we brand it and rely on the container's
    // box-sizing below to keep it inside the mobile viewport.
    form.setAttribute('spg-style', JSON.stringify({
      transaction: {
        layout: 'default',
        theme: 'default',
        color: {
          primary: theme.buttonColor || '',
          secondary: theme.buttonColor || '',
          border: '',
          surface: '',
          header: { text: '', background: '' },
          body: { text: '', background: '' },
        },
        font: '',
      },
    }));

    const script = document.createElement('script');
    script.src = spgCard.widgetScriptUrl;
    script.async = true;
    script.onload = () => setLoading(false);
    script.onerror = () => {
      setLoading(false);
      setError('card_payment_error');
    };
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [spgCard]);

  return (
    // Leave room under the SIBS form for the fixed logo bar (5vh plus 10px padding on each side)
    <div style={{ paddingBottom: 'calc(5vh + 20px)' }}>
      <Header
        showVm
        userLogo
        showBackButton
        backButtonColor={theme.buttonColor}
        onBack={onBack}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={5000}
        open={!!error}
        onClose={() => setError(null)}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {i18n.t(error as string)}
        </Alert>
      </Snackbar>

      <LoadingOverlay open={loading} messageId="loading" color={theme.buttonColor} />

      {spgCard?.formContext && (
        <>
          <h2 style={{ textAlign: 'center', color: theme.buttonColor, margin: '16px 0 0' }}>
            {i18n.t('payment_details')}
          </h2>
          <form
            ref={formRef}
            className="paymentSPG"
            style={{
              width: '100%',
              maxWidth: '100%',
              minHeight: '60vh',
              padding: '16px 0',
              boxSizing: 'border-box',
            }}
          />
        </>
      )}

      {theme.logo && (
        <LogoContainer>
          <img src={theme.logo} alt="logo" />
        </LogoContainer>
      )}
    </div>
  );
}

export default SpgCard;
